const API_BASE = '/api/v1';

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

interface ApiFetchOptions {
  body?: unknown;
  headers?: Record<string, string>;
}

let csrfToken: string | null = localStorage.getItem('sarnia_csrf');

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
  if (token) {
    localStorage.setItem('sarnia_csrf', token);
  } else {
    localStorage.removeItem('sarnia_csrf');
  }
}

export function getCsrfToken(): string | null {
  // In-memory after login; the server also sets a readable csrf_token cookie, which survives page reloads.
  return csrfToken ?? readCookie('csrf_token');
}

async function apiFetch<T>(method: HttpMethod, path: string, options: ApiFetchOptions = {}, retriedCsrf = false): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers ?? {})
  };

  if (options.body !== undefined && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Signed-in requests use the session's CSRF token; before login (sign in, sign up, verify) the server
  // hands out an anonymous token in the csrf_anon cookie, which must be echoed back in the header.
  const token = getCsrfToken() ?? readCookie('csrf_anon');
  if (token && method !== 'GET') {
    headers['x-csrf-token'] = token;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  });

  if (response.status === 204) {
    return undefined as T;
  }

  let data: unknown = null;
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    if (typeof data === 'object' && data !== null && 'error' in data) {
      const err = (data as { error: { code: string; message: string; details?: unknown } }).error;
      // A token left over from a session that has since expired (or whose cookies were cleared) no longer matches anything on
      // the server. Forget it and try once more with the anonymous token, so signing in again just works.
      if (err.code === 'CSRF_INVALID' && !retriedCsrf && method !== 'GET' && (csrfToken !== null || localStorage.getItem('sarnia_csrf') !== null)) {
        setCsrfToken(null);
        if (!readCookie('csrf_anon')) {
          // Any page or API GET hands out the anonymous token cookie.
          await fetch(`${API_BASE}/config`, { credentials: 'same-origin' }).catch(() => undefined);
        }
        if (readCookie('csrf_anon')) {
          return apiFetch<T>(method, path, options, true);
        }
      }
      throw new ApiError(response.status, err.code, err.message, err.details);
    }
    throw new ApiError(response.status, 'INTERNAL', 'Something went wrong.');
  }

  return data as T;
}

export interface AuthUser {
  id: string;
  email: string;
  role: 'customer' | 'operator';
  status: 'active' | 'suspended';
  emailVerified: boolean;
}

export interface SiteSummary {
  id: string;
  slug: string | null;
  state: 'draft' | 'published' | 'suspended';
  title: string;
  templateId: string;
  version: number;
  updatedAt: string;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>('GET', path),
  post: <T>(path: string, body?: unknown) => apiFetch<T>('POST', path, { body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>('PATCH', path, { body }),
  getMe: () => apiFetch<{ user: AuthUser }>('GET', '/auth/me'),
  signup: (payload: { email: string; password: string; fullName?: string }) =>
    apiFetch<{ message: string; email: string; verificationRequired?: boolean }>('POST', '/auth/signup', { body: payload }),
  login: (payload: { email: string; password: string }) =>
    apiFetch<{ user: AuthUser; csrfToken: string }>('POST', '/auth/login', { body: payload }),
  logout: () => apiFetch<{ ok: true }>('POST', '/auth/logout'),
  verifyEmail: (payload: { email: string; code: string }) =>
    apiFetch<{ ok: true }>('POST', '/auth/verify-email', { body: payload }),
  resendVerification: (email: string) =>
    apiFetch<{ ok: true }>('POST', '/auth/resend-verification', { body: { email } }),
  forgotPassword: (email: string) =>
    apiFetch<{ ok: true }>('POST', '/auth/forgot-password', { body: { email } }),
  resetPassword: (payload: { token: string; password: string }) =>
    apiFetch<{ ok: true }>('POST', '/auth/reset-password', { body: payload }),
  getSites: () => apiFetch<{ sites: SiteSummary[] }>('GET', '/sites'),
  createSite: (payload: { templateId: string; title?: string; slug?: string }) =>
    apiFetch<{ site: SiteSummary }>('POST', '/sites', { body: payload }),
  getSite: (siteId: string) => apiFetch<{ site: unknown; publishedUrl?: string }>('GET', `/sites/${siteId}`),
  patchSite: (siteId: string, payload: Record<string, unknown>) =>
    apiFetch<{ site: SiteSummary }>('PATCH', `/sites/${siteId}`, { body: payload }),
  deleteSite: (siteId: string) => apiFetch<{ ok: true }>('DELETE', `/sites/${siteId}`),
  checkSlug: (slug: string) => apiFetch<{ available: boolean; reason?: string }>('GET', `/reserved/check?slug=${encodeURIComponent(slug)}`),
  getAssets: (siteId: string) => apiFetch<{ assets: unknown[] }>('GET', `/sites/${siteId}/assets`),
  uploadAsset: (siteId: string, file: File, alt?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (alt) formData.append('alt', alt);
    return apiFetch<{ asset: unknown }>('POST', `/sites/${siteId}/assets`, {
      body: formData,
      headers: { Accept: 'application/json' }
    });
  },
  updateAsset: (assetId: string, payload: { alt?: string }) =>
    apiFetch<{ asset: unknown }>('PATCH', `/assets/${assetId}`, { body: payload }),
  deleteAsset: (assetId: string) => apiFetch<{ ok: true }>('DELETE', `/assets/${assetId}`),
  getBillingStatus: () =>
    apiFetch<{
      planId: string | null;
      status: string | null;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('GET', '/billing/status'),
  createBillingCheckout: (planId: string) =>
    apiFetch<{ url: string }>('POST', '/billing/checkout', { body: { planId } }),
  /** URL that downloads the clean website as a .zip (a normal link/anchor, the browser sends the login cookie). */
  exportUrl: (siteId: string) => `${API_BASE}/sites/${encodeURIComponent(siteId)}/export`,
  requestPlan: (payload: { planId: string; message?: string; siteId?: string }) =>
    apiFetch<{ request: { id: string; planId: string; status: string; alreadyOpen: boolean } }>('POST', '/billing/requests', { body: payload }),
  getMyPlanRequests: () =>
    apiFetch<{ requests: Array<{ id: string; plan_id: string; status: string; message: string; created_at: string }> }>('GET', '/billing/requests'),
  getPlanRequests: () =>
    apiFetch<{
      requests: Array<{
        id: string;
        plan_id: string;
        status: 'new' | 'contacted' | 'active' | 'declined';
        message: string;
        created_at: string;
        updated_at: string;
        user_email: string;
        user_name: string;
        site_id: string | null;
        site_title: string | null;
        site_slug: string | null;
      }>;
    }>('GET', '/admin/plan-requests'),
  updatePlanRequest: (id: string, status: 'new' | 'contacted' | 'active' | 'declined') =>
    apiFetch<{ ok: true }>('PATCH', `/admin/plan-requests/${encodeURIComponent(id)}`, { body: { status } }),
  createBillingPortal: () =>
    apiFetch<{ url: string }>('POST', '/billing/portal', { body: {} })
};
